/**
 * AIModal
 *
 * Collects an AI prompt and optional website to generate campaign details.
 * Handles basic validation, formatting, and shows generation progress/errors.
 */
import React, { useState, useEffect } from 'react';
import { X, Sparkles, Globe } from 'lucide-react';
import { Label } from '../../ui/inputs/label';
import { ButtonSpinner } from '../../ui/feedback/loading-spinner.jsx';

export function AIModal({ isOpen, onClose, aiPrompt, setAiPrompt, handleAIGenerate, isGenerating, aiError }) {
  const [sourceUrl, setSourceUrl] = useState('');
  const [formattedUrl, setFormattedUrl] = useState('');
  const [localError, setLocalError] = useState('');
  
  // Copy the external error to local state
  useEffect(() => {
    if (aiError) {
      setLocalError(aiError);
      
      // Auto-clear error after 5 seconds
      const timer = setTimeout(() => {
        setLocalError('');
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [aiError]);
  
  // Handle URL formatting to ensure proper format
  useEffect(() => {
    if (!sourceUrl) {
      setFormattedUrl('');
      return;
    }
    
    let processedUrl = sourceUrl.trim();
    
    // Remove protocol if user entered it
    processedUrl = processedUrl.replace(/^(https?:\/\/)?(www\.)?/, '');
    
    // Remove trailing slashes
    processedUrl = processedUrl.replace(/\/+$/, '');
    
    // Set the formatted URL with https:// prefix
    setFormattedUrl(`https://${processedUrl}`);
  }, [sourceUrl]);
  
  // Enhanced handleAIGenerate function that includes sourceUrl
  const handleSubmit = () => {
    // Pass both prompt and sourceUrl to the API
    handleAIGenerate(aiPrompt, formattedUrl);
  };
  
  // Check if either prompt or sourceUrl is filled
  const canSubmit = (aiPrompt && aiPrompt.trim()) || (sourceUrl && sourceUrl.trim());
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900 mx-4">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
        >
          <X className="h-5 w-5" />
        </button>
        
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/50">
              <Sparkles className="h-5 w-5 text-primary-text-600 dark:text-primary-text-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                AI Campaign Creator
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Describe your campaign and let AI do the work
              </p>
            </div>
          </div>
        </div>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="ai-prompt">Campaign Description</Label>
            <textarea
              id="ai-prompt"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="Example: Create a campaign for a local food bank's summer donation drive targeting young professionals..."
              className="mt-2 block h-32 w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-gray-900 placeholder:text-gray-500 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/10 dark:border-gray-800 dark:bg-gray-900 dark:text-white dark:placeholder:text-gray-400 dark:focus:border-primary-400 dark:focus:ring-primary-400/10"
            />
          </div>
          
          <div>
            <Label htmlFor="source-url" className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Website
            </Label>
            <div className="mt-2 flex items-center rounded-lg border border-gray-200 bg-white px-4 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white">
              <span className="text-gray-500 dark:text-gray-400">https://</span>
              <input
                id="source-url"
                type="text"
                value={sourceUrl.replace(/^(https?:\/\/)?(www\.)?/, '')}
                onChange={(e) => setSourceUrl(e.target.value)}
                placeholder="yourcompany.com"
                className="flex-1 border-0 bg-transparent py-3 focus:outline-none focus:ring-0 dark:text-white dark:placeholder:text-gray-400"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Provide your website URL to help the AI better understand your brand and generate relevant content
            </p>
          </div>
          
          {localError && (
            <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/50 dark:text-red-400">
              <X className="h-4 w-4" />
              {localError}
            </div>
          )}
          
          <div className="flex items-center gap-3 rounded-lg border border-primary-100 bg-primary-50/50 p-3 dark:border-primary-900/50 dark:bg-primary-900/10">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/50">
              <svg className="h-4 w-4 text-primary-text-600 dark:text-primary-text-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
              </svg>
            </div>
            <p className="text-sm text-primary-700 dark:text-primary-text-400">
              Our AI will generate campaign details, questions, and branding suggestions based on your description or website.
            </p>
          </div>
          
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit || isGenerating}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-500 disabled:bg-primary-500 disabled:opacity-50"
            >
              {isGenerating ? (
                <>
                  <ButtonSpinner color="white" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  Generate Campaign
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}